import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { DSCard, DSIcon, DSInput } from '../design-system/primitives';
import MachineBottomNav from '../components/MachineBottomNav';
import '../components/MachineBottomNav.css';
import './MachineManagement.css';

function MachineManagement() {
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadMachines();
  }, []);

  const loadMachines = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/machines');
      console.log('Loaded machines:', response.data.length, 'total');
      const removedCount = response.data.filter(m => m.status === 'removed').length;
      console.log('Removed machines:', removedCount);
      setMachines(response.data);
    } catch (err) {
      setError('Failed to load machines');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter machines by search term (partial match) and exclude removed machines
  const filteredMachines = machines.filter(machine => {
    // Exclude removed machines
    if (machine.status === 'removed') return false;
    
    // Apply search filter
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return machine.name.toLowerCase().includes(searchLower);
  });

  if (loading) return <div className="loading">Loading machines...</div>;

  return (
    <div className="machine-management bn-page-padding">
      {error && <div className="error-message">{error}</div>}

      {/* Search Bar */}
      <div className="search-container">
        <DSInput
          type="search"
          className="search-input"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="search-button">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM18 18l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Machine Grid */}
      <div className="machines-grid">
        {filteredMachines.map(machine => {
          const isDown = machine.status === 'out_of_order';
          const needsAttention = machine.status === 'needs_attention';

          return (
            <DSCard
              key={machine.id}
              cornerVariant="lg"
              className="machine-card"
              onClick={() => navigate(`/machines/${machine.id}`)}
            >
              <div className="machine-image-wrapper">
                {isDown && (
                  <span className="machine-status-flag machine-status-flag--down"><DSIcon name="dot" /> Down</span>
                )}
                {needsAttention && (
                  <span className="machine-status-flag machine-status-flag--attention"><DSIcon name="warning" /> Needs Attention</span>
                )}
                {(machine.image_url_large || machine.image_url_medium || machine.image_url) ? (
                  <img
                    src={machine.image_url_large || machine.image_url_medium || machine.image_url}
                    alt={machine.name}
                    className="machine-image"
                  />
                ) : (
                  <div className="machine-image-placeholder">
                    <span>No Image</span>
                  </div>
                )}
              </div>
              <div className="machine-name">{machine.name}</div>
            </DSCard>
          );
        })}
      </div>

      {filteredMachines.length === 0 && !loading && (
        <div className="empty-state">
          <p>{searchTerm ? 'No machines match your search.' : 'No machines found. Sync from MatchPlay in Developer Tools.'}</p>
        </div>
      )}

      <MachineBottomNav />
    </div>
  );
}

export default MachineManagement;
